import React, { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { getOverview, OverviewData, formatMicroUsd } from '../../lib/adminApi';

// Window options
type Window = '24h' | '7d' | '30d' | '90d';

function getDateRange(window: Window): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  if (window === '24h') {
    from.setTime(from.getTime() - 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
  }
  const days = window === '7d' ? 7 : window === '30d' ? 30 : 90;
  from.setDate(from.getDate() - days);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

function fmtShortDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="admin-stat-card">
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

const CHART_COLORS = ['#C87C34', '#4A3D33', '#A5621F', '#6B5B4E', '#9C8878', '#E5D9CB'];

function AdminDashboard() {
  const [window, setWindow] = useState<Window>('30d');
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async (w: Window) => {
    setLoading(true);
    setError('');
    try {
      const { from, to } = getDateRange(w);
      const result = await getOverview(from, to);
      setData(result);
    } catch {
      setError('Failed to load overview data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(window);
  }, [window, fetchData]);

  const handleWindow = (w: Window) => {
    setWindow(w);
  };

  const newUsers = (window === '7d' || window === '24h') ? data?.newUsers7d : data?.newUsers30d;
  const activeUsers = (window === '7d' || window === '24h') ? data?.activeUsers7d : data?.activeUsers30d;

  // Chart data transformations
  const signupsData = data?.signupsByDay.map(d => ({
    ...d,
    date: fmtShortDate(d.date),
  })) ?? [];

  const generationsData = data?.generationsByDay.map(d => ({
    ...d,
    date: fmtShortDate(d.date),
  })) ?? [];

  const creditsData = data?.creditsByDay.map(d => ({
    date: fmtShortDate(d.date),
    issued: Number(d.issued),
    consumed: Number(d.consumed),
  })) ?? [];

  const revenueData = data?.revenueByDay.map(d => ({
    date: fmtShortDate(d.date),
    usd: Number(d.microUsd) / 1_000_000,
  })) ?? [];

  const modelPieData = data?.modelUsage.map(m => ({
    name: m.model,
    value: m.totalTokens,
  })) ?? [];

  const statusPieData = data
    ? [
        { name: 'Succeeded', value: data.generations.succeeded },
        { name: 'Failed', value: data.generations.failed },
        {
          name: 'Other',
          value: Math.max(
            0,
            data.generations.total - data.generations.succeeded - data.generations.failed,
          ),
        },
      ].filter(d => d.value > 0)
    : [];

  const STATUS_COLORS = ['#C87C34', '#DC2626', '#9C8878'];

  return (
    <div>
      <div className="admin-topbar">
        <h1>Dashboard</h1>
      </div>
      <div className="admin-content">
        {/* Window selector */}
        <div className="admin-window-selector">
          {(['24h', '7d', '30d', '90d'] as Window[]).map(w => (
            <button
              key={w}
              className={`admin-window-btn${window === w ? ' active' : ''}`}
              onClick={() => handleWindow(w)}
            >
              {w}
            </button>
          ))}
        </div>

        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
            <div className="spinner" />
          </div>
        )}

        {error && (
          <div className="alert alert-error" role="alert">
            {error}
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* KPI stat cards */}
            <div className="admin-stat-grid">
              <StatCard label="Total Users" value={data.totalUsers} />
              <StatCard
                label={`New Users (${window})`}
                value={newUsers ?? '-'}
              />
              <StatCard
                label={`Active Users (${window})`}
                value={activeUsers ?? '-'}
              />
              <StatCard label="Credits Outstanding" value={data.creditsOutstanding} />
              <StatCard label="Total Generations" value={data.generations.total} />
              <StatCard
                label="Success Rate"
                value={`${data.generations.successRate.toFixed(1)}%`}
              />
              <StatCard label="Guest Sessions (30d)" value={data.guestSessionsStarted30d} />
              <StatCard label="Guest Conversions (30d)" value={data.guestConverted30d} />
              <StatCard
                label="Guest Conversion Rate"
                value={`${(data.guestConversionRate30d * 100).toFixed(1)}%`}
              />
            </div>

            {/* Charts */}
            <div className="admin-chart-grid">
              {/* Signups area chart */}
              <div className="admin-chart-card">
                <h3>Signups by Day</h3>
                <p>New user registrations</p>
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={signupsData}>
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke="#C87C34"
                      fill="#F3E4CF"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Generations stacked area */}
              <div className="admin-chart-card">
                <h3>Generations by Day</h3>
                <p>Succeeded vs. failed</p>
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={generationsData}>
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="succeeded"
                      stackId="1"
                      stroke="#C87C34"
                      fill="#F3E4CF"
                    />
                    <Area
                      type="monotone"
                      dataKey="failed"
                      stackId="1"
                      stroke="#DC2626"
                      fill="#FFEBEE"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Credits bar chart */}
              <div className="admin-chart-card">
                <h3>Credits by Day</h3>
                <p>Issued vs. consumed</p>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={creditsData}>
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="issued" fill="#C87C34" name="Issued" />
                    <Bar dataKey="consumed" fill="#4A3D33" name="Consumed" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Revenue line chart */}
              <div className="admin-chart-card">
                <h3>Revenue by Day</h3>
                <p>USD (converted from micro-USD)</p>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={revenueData}>
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${v.toFixed(2)}`} />
                    <Tooltip formatter={(v) => typeof v === 'number' ? `$${v.toFixed(4)}` : v} />
                    <Line
                      type="monotone"
                      dataKey="usd"
                      stroke="#C87C34"
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Model usage donut */}
              <div className="admin-chart-card">
                <h3>Model Usage</h3>
                <p>By total tokens</p>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={modelPieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name }) => name}
                    >
                      {modelPieData.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Generation status donut */}
              <div className="admin-chart-card">
                <h3>Generation Status</h3>
                <p>Succeeded / failed / other</p>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={statusPieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name }) => name}
                    >
                      {statusPieData.map((_, i) => (
                        <Cell key={i} fill={STATUS_COLORS[i % STATUS_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* AI usage by phase — includes object_detection calls, which are
                  free to users but still consume tokens */}
              <div className="admin-chart-card">
                <h3>AI Usage by Phase</h3>
                <p>Calls, tokens and cost per pipeline phase</p>
                <div className="admin-table-wrap" style={{ marginBottom: 0 }}>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Phase</th>
                        <th>Calls</th>
                        <th>Tokens</th>
                        <th>Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.phaseUsage ?? []).map(p => (
                        <tr key={p.phase}>
                          <td>{p.phase.replace(/_/g, ' ')}</td>
                          <td>{p.calls.toLocaleString()}</td>
                          <td>{p.totalTokens.toLocaleString()}</td>
                          <td>{formatMicroUsd(p.totalCostMicroUsd)}</td>
                        </tr>
                      ))}
                      {(data.phaseUsage ?? []).length === 0 && (
                        <tr><td colSpan={4} style={{ color: 'var(--text-faint)' }}>No AI calls in this window</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
